<script setup>
import { computed, nextTick, onMounted, ref, watch } from 'vue';
import { camelizeKeys } from 'humps';
import DetailBase from '@/app/community/components/DetailBase.vue';
import { Avatar, Button, Banner, Chip, useToast } from '@dashhudson/dashing-ui';
import InteractionActionMenu from '@/app/community/components/InteractionActionMenu.vue';
import { useVisionAiPermissions } from '@/components/VisionAi/composables/useVisionAiPermissions';
import VisionAiButton from '@/components/VisionAi/VisionAiButton.vue';
import AssignedTagGroup from '@/app/community/components/AssignedTagGroup.vue';
import {
  conversationFormatter,
  directLinkFormatter,
  messagesFormatter,
  isValidPlatformType,
  getDMPlatform,
} from '@/app/community/utils/dms';
import {
  COMMUNITY_DIRECT_LINK_TOOLTIPS,
  COMMUNITY_INTERACTION_ERROR_STATE_BANNERS,
  COMMUNITY_WEB_SOCKET_EVENTS,
  MESSAGE_BUBBLE_VARIANT_MAP,
  MESSAGE_REACTION_ERROR_MESSAGES,
  WEBSOCKET_UPDATE_ACTIONS,
} from '@/app/community/constants';
import ReplyComposer from '@/app/community/components/ReplyComposer.vue';
import ConversationMessage from '@/app/community/components/ConversationMessage.vue';
import { useCommunityTranslationStore } from '@/stores/community-translation';
import { useCommunityDmsStore } from '@/stores/community-dms';
import { useIdentityStore } from '@/stores/identity';
import { useSocketStore } from '@/stores/socket';
import { useCommunityInteractionStore } from '@/stores/community-interactions';
import useRouteQuery from '@/app/community/composables/useRouteQuery';
import { sortBy } from 'lodash-es';
import { useCommunityStore } from '@/stores/community';
import { useCommunityPermissions } from '@/app/community/composables/useCommunityPermissions';
import InteractionUtility from '@/app/community/components/InteractionUtility.vue';
import { useInteractionMenu } from '@/app/community/composables/useInteractionMenu';
import { useRoute } from 'vue-router';

const interactionStore = useCommunityInteractionStore();
const identityStore = useIdentityStore();
const dmStore = useCommunityDmsStore();
const translationStore = useCommunityTranslationStore();
const socketStore = useSocketStore();
const communityStore = useCommunityStore();

const { userHasVisionAiPermissionForCurrentRoute } = useVisionAiPermissions();
const { addToast } = useToast({ closable: true, life: 3000 });
const { isInternalNoteRoute, routeQueries, searchByAuthor } = useRouteQuery();

const emit = defineEmits(['refresh']);
const route = useRoute();

defineProps({
  interactionDetail: { type: Object, default: undefined },
});
const conversationMessageList = ref(null);

const selected = computed(() => interactionStore.selectedCommunityInteraction);
const conversation = computed(() => conversationFormatter(selected.value));
const { conversationHiddenMenu } = useInteractionMenu(conversation.value);

const showLatestButton = ref(false);
const selectedNote = computed(() => interactionStore.selectedInternalNote);
const paging = computed(() => dmStore.messagesPaging[conversation.value.id] ?? {});
const messages = computed(() => {
  const conversationMessages = dmStore.messages[conversation.value.id];
  if (conversationMessages?.length) {
    return messagesFormatter(conversationMessages, conversation.value.type);
  }
  return [];
});
const ruleExecutedInternalMessages = computed(() => {
  const ruleExecutionRecords =
    dmStore.triggeredMessageRules[interactionStore.selectedCommunityInteraction?.id] || [];

  return ruleExecutionRecords.map((record) => ({
    ...record,
    text: record.props.message,
    action: record.props.action,
    avatar: {
      icon: 'dash-social',
      variant: 'brand',
    },
  }));
});

const displayVisionAiButton = computed(
  () => userHasVisionAiPermissionForCurrentRoute && !route.meta?.isSalesforceEmbed,
);
const internalMessages = computed(() =>
  communityStore.formattedInternalMessages.map((internalMessage) => ({
    ...internalMessage,
    text: internalMessage.props.message,
    avatar: {
      label: internalMessage.props.sender.initials,
      tooltip: internalMessage.props.sender.fullName,
    },
  })),
);
const combinedMessages = computed(() =>
  sortBy(
    [...messages.value, ...internalMessages.value, ...ruleExecutedInternalMessages.value],
    (message) => message.createdAt,
  ),
);
const author = computed(() => conversation.value.followerParticipant);
const directLink = computed(() => directLinkFormatter(conversation.value));
const directLinkTooltip = computed(() => COMMUNITY_DIRECT_LINK_TOOLTIPS[conversation.value.type]);
const errorStatus = computed(() => {
  const { type, errorStatus: error } = conversation.value ?? {};
  if (error) {
    return COMMUNITY_INTERACTION_ERROR_STATE_BANNERS[type][error];
  }
  return undefined;
});
const isAuthorBlocked = computed(() => conversation.value?.isAuthorBlocked);
const loading = computed(() => dmStore.pending.messages || dmStore.pending.messagesPaging);

function formatMessage(message) {
  const lastSeenMessage = messages.value.filter((m) => m.seenAt).pop();
  const variant = MESSAGE_BUBBLE_VARIANT_MAP[message.resourceType] || 'default';
  return {
    ...message,
    seen: lastSeenMessage?.id === message.id,
    sentByBrand: message.fromParticipantId === conversation.value.brandParticipantId,
    variant,
  };
}

function onLinkClicked() {
  if (directLink.value) {
    window.open(directLink.value, '_blank');
  }
}

async function loadNextPage(oldScrollHeight) {
  if (paging.value.next && !loading.value) {
    await dmStore.getPlatformMessagesNextPage({ url: paging.value.next });
    await nextTick(() => {
      if (conversationMessageList.value) {
        const { scrollHeight } = conversationMessageList.value;
        conversationMessageList.value.scrollTop = scrollHeight - oldScrollHeight;
      }
    });
  }
}

function scrollToMostRecent() {
  nextTick(() => {
    if (!conversationMessageList.value) return;

    // First scroll to last message if available
    const messageList = conversationMessageList.value.querySelectorAll('[data-message-id]');
    if (messageList.length) {
      const lastMessage = messageList[messageList.length - 1];
      nextTick(() => {
        lastMessage.scrollIntoView({ behavior: 'auto', block: 'end' });
      });
    }

    if (conversationMessageList.value) {
      conversationMessageList.value.scrollTop = conversationMessageList.value.scrollHeight;
    }
  });
}

function updateShowLatestButton() {
  const el = conversationMessageList.value;
  if (!el) return;
  const atBottom = Math.abs(el.scrollHeight - el.scrollTop - el.clientHeight) < 600;
  showLatestButton.value = !atBottom;
}

async function handleScroll() {
  updateShowLatestButton();
  if (conversationMessageList.value) {
    const { scrollHeight, scrollTop } = conversationMessageList.value;
    if (scrollTop < scrollHeight / 10) {
      await loadNextPage(scrollHeight * 0.9);
    }
  }
}

async function handleMessageUpdates(payload) {
  const { brandId, updates } = camelizeKeys(payload);
  const shouldUpdate =
    routeQueries.value.brandIds?.includes(brandId) || brandId === identityStore.currentBrand.id;

  if (shouldUpdate && brandId) {
    const { data = {}, action } = updates[0];
    const { type: messageType } = data;
    if (isValidPlatformType(messageType)) {
      await dmStore.bulkUpdatePlatformMessages({
        platform: getDMPlatform(messageType),
        updates,
      });
      if (action === WEBSOCKET_UPDATE_ACTIONS.CREATE) {
        // only scroll to bottom when receiving messages via web socket
        scrollToMostRecent();
      }
    }
  }
}

function handleReactionFailed(payload) {
  const { type, message } = payload ?? {};
  if (isValidPlatformType(message?.type)) {
    addToast({
      message: MESSAGE_REACTION_ERROR_MESSAGES[type],
      severity: 'error',
    });
    dmStore.updatePlatformMessageLocally({
      platform: getDMPlatform(message.type),
      updatedMessage: message,
    });
  }
}

function handleLatestMessageClicked() {
  scrollToMostRecent();
  showLatestButton.value = false;
  emit('refresh');
}

function handleMediaLoaded() {
  if (!selectedNote.value && !paging.value.previous) {
    // Media can load after we've already scrolled to the most recent message on load,
    // causing us to not be scrolled to the bottom as the div has been expanded. However,
    // if we've loaded a new page, don't scroll to the bottom
    scrollToMostRecent();
  }
}

watch(
  () => messages.value?.length,
  async (to) => {
    if (to > 0) {
      const simplified = messages.value.map((m) => ({ id: m.id, text: m.text }));
      await translationStore.detectLanguages(simplified);
    }
  },
  { immediate: true },
);

watch(
  () => socketStore?.id,
  (to) => {
    if (to) {
      socketStore.addListener(
        COMMUNITY_WEB_SOCKET_EVENTS.MESSAGES_UPDATED,
        handleMessageUpdates,
        null,
      );
      socketStore.addListener(
        COMMUNITY_WEB_SOCKET_EVENTS.REACTION_FAILED,
        handleReactionFailed,
        null,
      );
    }
  },
  { immediate: true },
);

onMounted(async () => {
  if (selectedNote.value) {
    const noteElementId = `${selectedNote.value?.resourceId}_${selectedNote.value?.id}`;
    const el = conversationMessageList.value?.querySelector(`[data-message-id="${noteElementId}"]`);
    if (el) {
      conversationMessageList.value.scrollTop =
        el.offsetTop - conversationMessageList.value.offsetTop;
    }
  } else {
    scrollToMostRecent();
  }
});
</script>

<template>
  <div
    v-if="selected && messages.length"
    class="dash-flex dash-w-full dash-h-full dash-min-h-0 dash-gap-2 dash-items-start !pr-10"
  >
    <DetailBase>
      <template #header>
        <div class="dash-flex dash-flex-col dash-gap-4">
          <div class="dash-flex dash-justify-between dash-items-center">
            <div class="dash-flex dash-items-center dash-gap-2">
              <div
                v-dash-tooltip="!isInternalNoteRoute ? 'Search all messages from user' : undefined"
                :class="[
                  'dash-flex dash-items-center dash-gap-2',
                  { 'dash-cursor-pointer': !isInternalNoteRoute },
                ]"
              >
                <Avatar :image="author?.profilePictureUrl" />
                <div
                  class="dash-text-primary dash-typo-base-medium"
                  data-test="author-name"
                  @click="searchByAuthor(author?.username)"
                >
                  {{ author?.username }}
                </div>
              </div>
              <Button
                v-if="directLink"
                v-dash-tooltip="directLinkTooltip"
                icon="link-external"
                size="xs"
                severity="tertiary"
                @click="onLinkClicked"
              />
              <Chip v-if="isAuthorBlocked" severity="danger" size="sm" label="Blocked" />
            </div>
            <VisionAiButton v-if="displayVisionAiButton" />
          </div>
          <AssignedTagGroup />
        </div>
      </template>
      <template #content>
        <div class="dash-flex-1 dash-min-h-0">
          <div
            ref="conversationMessageList"
            data-conversation-list
            class="dash-flex dash-flex-col dash-gap-4 dash-h-full dash-overflow-x-hidden dash-overflow-y-auto !pb-4 !pr-1"
            @scroll="handleScroll"
          >
            <template v-for="message in combinedMessages" :key="`message-${message.id}`">
              <ConversationMessage
                :data-message-id="message.id"
                :interaction="conversation"
                :message="formatMessage(message)"
                @media-loaded="handleMediaLoaded"
              >
                {{ message.text }}
              </ConversationMessage>
            </template>
          </div>
        </div>
      </template>
      <template #footer>
        <div
          class="dash-w-full dash-pb-6 dash-flex dash-flex-col dash-gap-4 dash-relative dash-pr-8 dash-shrink-0 dash-sticky dash-bottom-0 dash-z-10 dash-bg-white"
        >
          <Button
            v-if="interactionDetail?.hasNewerMessages || showLatestButton"
            icon="arrow-down"
            class="dash-absolute dash-left-1/2 dash-translate-x-1/2 dash-z-10 -mt-16"
            @click="handleLatestMessageClicked"
          />
          <InteractionUtility
            :interaction="conversation"
            :hidden-menu-items="conversationHiddenMenu"
          />
          <Banner
            v-if="errorStatus"
            severity="warn"
            :message="errorStatus.text"
            :closable="false"
          />
          <ReplyComposer
            class="dash-relative"
            :interaction="conversation"
            :disabled="errorStatus !== undefined"
          />
        </div>
      </template>
    </DetailBase>
  </div>
</template>
<style scoped>
:global(.hs-web-interactives-top-banner-open [data-conversation-list]) {
  padding-bottom: calc(1rem + var(--hubspot-banner-height, 0px)) !important;
}
</style>
